import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, Phone, CheckCircle2, Sparkles, PencilRuler, Hammer, ShieldCheck, ChevronRight } from "lucide-react";
import { SITE, COMPLETED_PROJECTS } from "../data/site";
import { SERVICES, SERVICE_LIST } from "../data/services";
import { usePageMeta } from "../hooks/usePageMeta";

function findProject(id) {
  return COMPLETED_PROJECTS.find((p) => p.id === id) || COMPLETED_PROJECTS[0];
}

export default function ServicePage() {
  const { service } = useParams();
  const data = SERVICES[service];
  usePageMeta({
    title: data?.seo_title,
    description: data?.meta_description,
    canonicalPath: `/services/${service}`,
  });
  if (!data) return <Navigate to="/services" replace />;

  const project = findProject(data.hero_image);

  return (
    <div className="min-h-screen bg-[#050505] text-white pt-24 pb-16 grain">
      <div className="container-x px-5 md:px-10 lg:px-16">
        {/* Breadcrumb */}
        <div className="text-[10px] tracking-[0.28em] uppercase text-[#A1A1AA] mb-6">
          <Link to="/" className="hover:text-[#D4AF37]">Home</Link>{" "}
          / <Link to="/services" className="hover:text-[#D4AF37]">Services</Link>{" "}
          / <span className="text-[#D4AF37]">{data.title}</span>
        </div>

        {/* Hero */}
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#D4AF37]">{data.overline}</div>
            <h1 data-testid="service-heading" className="font-display text-4xl md:text-5xl lg:text-6xl leading-tight mt-4">
              {data.hero_heading}
            </h1>
            <p className="text-sm md:text-base text-[#D1D1D6] mt-5 leading-relaxed max-w-2xl">
              {data.hero_sub}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/design-my-wall"
                data-testid={`service-cta-design-${data.slug}`}
                className="btn-gold !py-3 !px-6"
              >
                <Sparkles className="w-4 h-4" /> Design My Wall <ArrowRight className="w-4 h-4" />
              </Link>
              <a href={`tel:${SITE.phoneDigits}`} className="btn-ghost !py-3 !px-6">
                <Phone className="w-4 h-4" /> Request Consultation
              </a>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-2xl border border-[#27272A] overflow-hidden">
              <img
                src={project.src}
                alt={data.title}
                className="w-full aspect-[4/3] object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </div>

        {/* Features */}
        <section className="mt-20 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#D4AF37]">What's included</div>
            <h2 className="font-display text-3xl md:text-4xl mt-3 leading-tight">
              Every {data.title.toLowerCase()} we build includes:
            </h2>
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-3">
            {data.features.map((f, i) => (
              <div
                key={i}
                data-testid={`feature-${data.slug}-${i}`}
                className="rounded-xl border border-[#27272A] bg-[#0d0d0d] p-4 flex items-start gap-3"
              >
                <CheckCircle2 className="flex-none w-4 h-4 text-[#D4AF37] mt-0.5" />
                <div className="text-sm text-[#D1D1D6] leading-relaxed">{f}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Materials */}
        <section className="mt-20">
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#D4AF37] mb-3">Materials & finishes</div>
          <h2 className="font-display text-3xl md:text-4xl">Combinations we design around</h2>
          <p className="text-sm text-[#A1A1AA] mt-3 max-w-3xl leading-relaxed">{data.materials_intro}</p>
          <div className="mt-8 grid sm:grid-cols-2 gap-3">
            {data.materials.map((m, i) => (
              <div
                key={i}
                className="rounded-xl border border-[#27272A] bg-[#0d0d0d] p-5 flex items-start gap-3"
              >
                <ChevronRight className="flex-none w-4 h-4 text-[#D4AF37] mt-0.5" />
                <div className="text-sm text-[#D1D1D6] leading-relaxed">{m}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Process */}
        <section className="mt-20">
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#D4AF37] mb-3">How it goes</div>
          <h2 className="font-display text-3xl md:text-4xl">From consult to finish</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
            {data.process.map((step, i) => {
              const Icon = [PencilRuler, ShieldCheck, Hammer, CheckCircle2][i] || CheckCircle2;
              return (
                <div key={i} className="rounded-2xl border border-[#27272A] bg-[#0d0d0d] p-6 relative">
                  <div className="absolute top-4 right-4 text-[10px] tracking-[0.24em] uppercase text-[#6b6b72]">
                    0{i + 1}
                  </div>
                  <Icon className="w-6 h-6 text-[#D4AF37]" />
                  <div className="text-sm text-[#D1D1D6] mt-4 leading-relaxed">{step}</div>
                </div>
              );
            })}
          </div>
        </section>

        {/* FAQ */}
        <section className="mt-20">
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#D4AF37] mb-3">Common questions</div>
          <h2 className="font-display text-3xl md:text-4xl mb-8">{data.title} FAQs</h2>
          <div className="space-y-3 max-w-3xl">
            {data.faqs.map((f, i) => (
              <details
                key={i}
                data-testid={`svc-faq-${data.slug}-${i}`}
                className="group rounded-xl border border-[#27272A] bg-[#0d0d0d] open:border-[#AA8035] transition-colors"
              >
                <summary className="cursor-pointer list-none px-6 py-5 flex items-start justify-between gap-6">
                  <div className="font-display text-lg text-white">{f.q}</div>
                  <span className="flex-none mt-1 text-[#D4AF37] text-xl group-open:rotate-45 transition-transform">+</span>
                </summary>
                <div className="px-6 pb-5 text-sm text-[#D1D1D6] leading-relaxed">{f.a}</div>
              </details>
            ))}
          </div>
        </section>

        {/* Related services */}
        <section className="mt-20">
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#D4AF37] mb-3">Related services</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {SERVICE_LIST.filter((s) => s !== data.slug).map((slug) => {
              const s = SERVICES[slug];
              return (
                <Link
                  key={slug}
                  to={`/services/${slug}`}
                  data-testid={`related-service-${slug}`}
                  className="rounded-xl border border-[#27272A] bg-[#0d0d0d] p-5 hover:border-[#AA8035] transition-colors"
                >
                  <div className="text-[10px] tracking-[0.24em] uppercase text-[#D4AF37]">{s.overline}</div>
                  <div className="font-display text-lg text-white mt-2">{s.title}</div>
                  <div className="text-[11px] text-[#A1A1AA] mt-2">Explore →</div>
                </Link>
              );
            })}
          </div>
        </section>

        {/* CTA */}
        <section className="mt-20 rounded-3xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#0a0a0a] via-[#111] to-[#050505] p-10 md:p-14 text-center relative overflow-hidden">
          <div
            className="absolute inset-0 opacity-40 pointer-events-none"
            style={{ background: "radial-gradient(circle at 50% 0%, rgba(212,175,55,0.14) 0%, transparent 60%)" }}
          />
          <Sparkles className="w-8 h-8 text-[#D4AF37] mx-auto" />
          <h2 className="font-display text-3xl md:text-5xl mt-4">Ready to design your {data.title.toLowerCase()}?</h2>
          <p className="text-sm md:text-base text-[#A1A1AA] mt-4 max-w-xl mx-auto">
            Answer a few visual questions and Rob will review your selections personally.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to="/design-my-wall" className="btn-gold !py-3 !px-6">Design My Wall</Link>
            <a href={`tel:${SITE.phoneDigits}`} className="btn-ghost !py-3 !px-6">
              <Phone className="w-4 h-4" /> Call {SITE.phone}
            </a>
          </div>
        </section>
      </div>
    </div>
  );
}
